import type { ActionReducerMapBuilder, EntityState } from '@reduxjs/toolkit';
import { isNotDefined } from '@sgme/fp';

import { OMSOrderTypeFromBlotterOrderType } from '@/store/blotter/api/types/BlotterOrderEntryFromBackend.ts';
import { summonOrder } from '@/store/blotter/workflows/summonOrder/action.ts';
import { ORDER_GENERATOR_FROM_BLOTTER_BY_TYPE } from '@/store/orders/helpers.ts';
import type { OrderById } from '@/store/orders/state.ts';
import { tabsAdapter } from '@/store/tabs/adapter.ts';
import type { TabsState } from '@/store/tabs/types.ts';
import { generateDefaultTilesTabName } from '@/store/tabs/utils.ts';
import { tilesAdapter } from '@/store/tiles/adapter.ts';
import type { Tile } from '@/store/tiles/types.ts';

//
// ████████╗ █████╗ ██████╗ ███████╗    ███████╗██╗     ██╗ ██████╗███████╗
// ╚══██╔══╝██╔══██╗██╔══██╗██╔════╝    ██╔════╝██║     ██║██╔════╝██╔════╝
//    ██║   ███████║██████╔╝███████╗    ███████╗██║     ██║██║     █████╗
//    ██║   ██╔══██║██╔══██╗╚════██║    ╚════██║██║     ██║██║     ██╔══╝
//    ██║   ██║  ██║██████╔╝███████║    ███████║███████╗██║╚██████╗███████╗
//    ╚═╝   ╚═╝  ╚═╝╚═════╝ ╚══════╝    ╚══════╝╚══════╝╚═╝ ╚═════╝╚══════╝
//

export function summonOrderTabsReducer(builder: ActionReducerMapBuilder<TabsState>) {
  builder.addCase(summonOrder, (state, { payload: { entry, tabId } }) => {
    const allTabs = tabsAdapter.getSelectors().selectAll(state.tabs);

    if (isNotDefined(entry.clientId)) {
      return;
    }

    tabsAdapter.addOne(state.tabs, {
      id: tabId,
      tabType: 'tiles',
      clientId: entry.clientId,
      tabName: generateDefaultTilesTabName(allTabs),
      isShared: false,
    });

    state.activeTabId = tabId;
  });
}

// ████████╗██╗██╗     ███████╗███████╗    ███████╗██╗     ██╗ ██████╗███████╗
// ╚══██╔══╝██║██║     ██╔════╝██╔════╝    ██╔════╝██║     ██║██╔════╝██╔════╝
//    ██║   ██║██║     █████╗  ███████╗    ███████╗██║     ██║██║     █████╗
//    ██║   ██║██║     ██╔══╝  ╚════██║    ╚════██║██║     ██║██║     ██╔══╝
//    ██║   ██║███████╗███████╗███████║    ███████║███████╗██║╚██████╗███████╗
//    ╚═╝   ╚═╝╚══════╝╚══════╝╚══════╝    ╚══════╝╚══════╝╚═╝ ╚═════╝╚══════╝
//
export function summonOrderTilesReducer(builder: ActionReducerMapBuilder<EntityState<Tile, string>>) {
  builder.addCase(summonOrder, (state, { payload: { tabId, tileId, entry } }) => {
    if (isNotDefined(entry.clientId)) {
      return;
    }

    if (entry.orderType === 'Lyrebird') {
      // should have been prevented earlier, no Lyrebird order is editable
      console.error('Cannot summon order tile for Lyrebird orderType');
      return;
    }

    tilesAdapter.addOne(state, {
      tileId,
      tabId,
      productType: OMSOrderTypeFromBlotterOrderType[entry.orderType],
      x: 0,
      y: 0,
      h: 1,
      w: 1,
    });
  });
}

//  ██████╗ ██████╗ ██████╗ ███████╗██████╗ ███████╗    ███████╗██╗     ██╗ ██████╗███████╗
// ██╔═══██╗██╔══██╗██╔══██╗██╔════╝██╔══██╗██╔════╝    ██╔════╝██║     ██║██╔════╝██╔════╝
// ██║   ██║██████╔╝██║  ██║█████╗  ██████╔╝███████╗    ███████╗██║     ██║██║     █████╗
// ██║   ██║██╔══██╗██║  ██║██╔══╝  ██╔══██╗╚════██║    ╚════██║██║     ██║██║     ██╔══╝
// ╚██████╔╝██║  ██║██████╔╝███████╗██║  ██║███████║    ███████║███████╗██║╚██████╗███████╗
//  ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚══════╝╚═╝  ╚═╝╚══════╝    ╚══════╝╚══════╝╚═╝ ╚═════╝╚══════╝

export function summonOrderOrdersReducer(builder: ActionReducerMapBuilder<OrderById>) {
  builder.addCase(summonOrder, (state, { payload: { entry, tileId, mode } }) => {
    if (isNotDefined(entry.clientId)) {
      return;
    }

    if (entry.orderType === 'Lyrebird') {
      // should have been prevented earlier, no Lyrebird order is editable
      console.error('Cannot summon order tile for Lyrebird orderType');
      return state;
    }

    const OMSOrderType = OMSOrderTypeFromBlotterOrderType[entry.orderType];

    return {
      ...state,
      [tileId]: ORDER_GENERATOR_FROM_BLOTTER_BY_TYPE[OMSOrderType](entry, mode),
    };
  });
}
